import {ICheckinCycleContent} from '@pages/dashboard/company-legacy/company-components/company-checkins/company-checkins.interface';
import {ContentBlock} from './types';
import {ContentSummary} from './preview-step.types';
import {GOALS_REFLECTION_PROMPT} from './goals-content';
import {
  DEFAULT_PULSE_EMOJIS,
  DEFAULT_PULSE_VALUES,
} from '../../shared/pulse-scale';

const isQuestionBlock = (
  block: ContentBlock,
): block is Extract<ContentBlock, {type: 'question'}> =>
  block.type === 'question';

const isChecklistBlock = (
  block: ContentBlock,
): block is Extract<ContentBlock, {type: 'checklist'}> =>
  block.type === 'checklist';

const isPulseBlock = (
  block: ContentBlock,
): block is Extract<ContentBlock, {type: 'pulse'}> => block.type === 'pulse';

const isGoalsBlock = (
  block: ContentBlock,
): block is Extract<ContentBlock, {type: 'goals'}> => block.type === 'goals';

export const buildContentFromBlocks = (blocks: ContentBlock[]) => {
  const content: ICheckinCycleContent = {};
  const params: string[] = [];

  const questionBlocks = blocks.flatMap((block) => {
    if (isQuestionBlock(block)) {
      const question = block.questionData?.text || '';
      return question
        ? [
            {
              question,
              required: !(block.questionData?.optional ?? true),
              answerType: block.questionData?.answerType || 'text',
            },
          ]
        : [];
    }

    if (isGoalsBlock(block)) {
      return [
        {
          question: GOALS_REFLECTION_PROMPT,
          required: false,
          answerType: 'text',
        },
      ];
    }

    return [];
  });
  const regularQuestionCount = blocks
    .filter(isQuestionBlock)
    .filter((block) => Boolean(block.questionData?.text)).length;
  const hasGoals = blocks.some(isGoalsBlock);

  if (questionBlocks.length) {
    content.questions = questionBlocks;
    params.push('questions');
  }

  const checklistBlocks = blocks
    .filter(isChecklistBlock)
    .map((block) => block.checklistData)
    .filter(Boolean)
    .map((data) => ({
      title: data?.title || 'Checklist',
      checklist:
        data?.checklist
          ?.map((item: {text: string}) => item.text)
          .filter(Boolean) || [],
    }))
    .filter((item) => item.title || item.checklist.length);

  if (checklistBlocks.length) {
    content.priorities = checklistBlocks;
    params.push('priorities');
  }

  const pulseBlocks = blocks
    .filter(isPulseBlock)
    .map((block) => block.pulseData)
    .filter(Boolean)
    .map((data) => ({
      name: data?.name || 'Pulse check-in',
      options:
        data?.options?.length && data?.options.filter(Boolean).length
          ? data.options
          : DEFAULT_PULSE_VALUES,
      emojis:
        data?.emojis?.length && data?.emojis.filter(Boolean).length
          ? data.emojis
          : DEFAULT_PULSE_EMOJIS,
    }));

  if (pulseBlocks.length) {
    content.pulse = pulseBlocks;
    params.push('pulse');
  }

  return {
    content,
    params,
    summary: {
      questions: regularQuestionCount,
      checklists: checklistBlocks.length,
      hasPulse: pulseBlocks.length > 0,
      ...(hasGoals ? {hasGoals: true} : {}),
    } as ContentSummary,
  };
};
